import classNames from 'classnames';
import React, { createContext, useContext, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { t } from '../../../i18n/translator';

import styles from './Modal.module.scss';

// The host adapter can present the source wheel as one document inside an
// auto-sized iframe. Only the primary overlay opts in; sibling dialogs stay modal.
export const ModalPageContext = createContext(false);

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    /** Custom visible header; title remains the accessible dialog name. */
    titleContent?: React.ReactNode;
    size?: 'sm' | 'md' | 'lg';
    variant?: 'default' | 'image';
    children: React.ReactNode;
    footer?: React.ReactNode;
    className?: string;
    overlayClassName?: string;
}

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    titleContent,
    size = 'md',
    variant = 'default',
    children,
    footer,
    className,
    overlayClassName,
}) => {
    const documentFlow = useContext(ModalPageContext) && Boolean(overlayClassName);
    const handleEscape = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        },
        [onClose],
    );

    useEffect(() => {
        if (!isOpen) return;
        document.addEventListener('keydown', handleEscape);
        if (!documentFlow) document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleEscape);
            if (!documentFlow) document.body.style.overflow = '';
        };
    }, [isOpen, handleEscape, documentFlow]);

    if (!isOpen) return null;

    return createPortal(
        <div className={classNames(styles.overlay, overlayClassName, {[styles.pageOverlay]: documentFlow})} data-extra-page-modal={documentFlow ? '' : undefined} role="dialog" aria-modal="true" aria-label={title || 'Modal'}>
            <button
                type="button"
                className={styles.backdrop}
                onClick={onClose}
                aria-label={t(2635)}
            />
            <div
                className={classNames(styles.modal, styles[size], {
                    [styles.imageModal]: variant === 'image',
                    [styles.pageModal]: documentFlow,
                }, className)}
            >
                <div className={styles.header}>
                    {titleContent !== undefined ? titleContent : title && variant !== 'image' && <h2 className={styles.title}>{title}</h2>}
                    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t(2636)}>
                        ✕
                    </button>
                </div>
                <div className={styles.body}>{children}</div>
                {footer && <div className={styles.footer}>{footer}</div>}
            </div>
        </div>,
        document.body,
    );
};

export default Modal;
